import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Badge from 'flarum/common/components/Badge';
import extractText from 'flarum/common/utils/extractText';
import classList from 'flarum/common/utils/classList';
import { money, rangeLabel, unit } from '../../common/api';
import LadderBanner from '../../common/components/LadderBanner';
import { loadLadder } from '../ladderStore';
import RankMembersModal from './RankMembersModal';
const t = function (name, params) {
  if (params === void 0) {
    params = {};
  }
  return app.translator.trans(`ernestdefoe-ladder.forum.ranks.${name}`, params);
};

/**
 * /ranks: every rung, what it takes, and where the reader stands.
 *
 * Generated from the ladder itself, so it can't go stale the way a
 * hand-written "here are our ranks" post does the first time a threshold
 * changes.
 */
export default class RanksPage extends Page {
  ladder = null;
  oninit(vnode) {
    super.oninit(vnode);
    app.history.push('ladder.ranks', extractText(t('title')));
    loadLadder().then(ladder => {
      this.ladder = ladder;
      m.redraw();
    });
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    app.setTitle(extractText(t('title')));
    app.setTitleCount(0);
  }
  view() {
    return m(PageStructure, {
      className: "LadderRanksPage",
      hero: () => this.hero(),
      sidebar: () => m(IndexSidebar, null)
    }, m("div", {
      className: "LadderRanks"
    }, m("header", {
      className: "LadderRanks-header"
    }, m("h2", {
      className: "LadderRanks-title"
    }, t('title')), this.ladder && m("p", {
      className: "LadderRanks-intro"
    }, t(unit('intro', this.ladder.metric)))), this.ladder === null ? m(LoadingIndicator, null) : this.body(this.ladder)));
  }
  hero() {
    if (!this.ladder?.rungs.length) return null;
    return m("div", {
      className: "LadderRanksPage-hero container"
    }, m(LadderBanner, {
      ladder: this.ladder,
      currentGroupId: this.ladder.viewer?.groupId
    }));
  }
  body(ladder) {
    if (!ladder.rungs.length) {
      return m("p", {
        className: "LadderRanks-empty"
      }, t('empty'));
    }
    return [this.standing(ladder), m("ol", {
      className: "LadderRanks-list"
    }, ladder.rungs.map(rung => this.rung(rung, ladder.viewer?.groupId === rung.groupId)))];
  }

  /** The reader's own place on the ladder, and how far it is to the next rung. */
  standing(ladder) {
    const viewer = ladder.viewer;
    if (!viewer) {
      return m("div", {
        className: "LadderRanks-standing LadderRanks-standing--guest"
      }, t('guest'));
    }
    if (viewer.exempt) {
      return m("div", {
        className: "LadderRanks-standing LadderRanks-standing--guest"
      }, t('exempt'));
    }
    const rungs = ladder.rungs;
    const index = rungs.findIndex(rung => rung.groupId === viewer.groupId);
    const current = index >= 0 ? rungs[index] : null;

    // The next rung is the first one above both the rank held and the posts
    // made. With demotion off a member can hold a rank above their count.
    const floor = current ? current.minPosts : -1;
    const next = rungs.find(rung => rung.minPosts > floor && rung.minPosts > viewer.score) ?? null;
    let percent = 100;
    if (next) {
      // A member can hold a rank above their own count (demotion off, a
      // threshold raised), so measure from whichever is lower.
      const from = Math.min(current ? current.minPosts : 0, viewer.score);
      const span = Math.max(1, next.minPosts - from);
      percent = Math.max(0, Math.min(100, Math.round((viewer.score - from) / span * 100)));
    }
    const remaining = next ? next.minPosts - viewer.score : 0;
    return m("div", {
      className: "LadderRanks-standing"
    }, m("div", {
      className: "LadderRanks-standingText"
    }, current && m("p", null, t(unit('your_rank', ladder.metric), {
      rank: m("strong", null, current.name),
      count: viewer.score,
      amount: money(viewer.score)
    })), m("p", null, next ? current ? t(unit('next', ladder.metric), {
      count: remaining,
      amount: money(remaining),
      rank: m("strong", null, next.name)
    }) : t(unit('unranked', ladder.metric), {
      count: remaining,
      amount: money(remaining),
      rank: m("strong", null, next.name)
    }) : t('top'))), next && m("div", {
      className: "LadderRanks-meter",
      role: "progressbar",
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-valuenow": percent,
      style: next.color ? {
        '--ladder-next': next.color
      } : undefined
    }, m("div", {
      className: "LadderRanks-meterFill",
      style: {
        width: `${percent}%`
      }
    })));
  }
  rung(rung, isMine) {
    return m("li", {
      className: classList('LadderRanks-rung', isMine && 'LadderRanks-rung--mine'),
      style: rung.color ? {
        '--ladder-color': rung.color
      } : undefined
    }, m("span", {
      className: "LadderRanks-badge"
    }, m(Badge, {
      icon: rung.icon || 'fas fa-circle',
      color: rung.color || undefined
    })), m("div", {
      className: "LadderRanks-main"
    }, m("div", {
      className: "LadderRanks-nameLine"
    }, m("span", {
      className: "LadderRanks-name"
    }, rung.name), isMine && m("span", {
      className: "LadderRanks-here"
    }, t('you_are_here'))), rung.description && m("p", {
      className: "LadderRanks-description"
    }, rung.description)), m("div", {
      className: "LadderRanks-meta"
    }, m("span", {
      className: "LadderRanks-range"
    }, rangeLabel('ernestdefoe-ladder.forum.ranks', rung, this.ladder?.metric)), this.ladder?.canListMembers && rung.memberCount > 0 ? m("button", {
      type: "button",
      className: "LadderRanks-members LadderRanks-members--link",
      onclick: () => app.modal.show(RankMembersModal, {
        rung,
        metric: this.ladder.metric
      })
    }, t('members', {
      count: rung.memberCount
    }), m("i", {
      className: "fas fa-chevron-right",
      "aria-hidden": "true"
    })) : m("span", {
      className: "LadderRanks-members"
    }, t('members', {
      count: rung.memberCount
    }))));
  }
}
flarum.reg.add('ernestdefoe-ladder', 'forum/components/RanksPage', RanksPage);